import "babel-polyfill";
import React, { Component } from "react";
import PropTypes from "prop-types";
import * as d3 from "d3";
import { Redirect } from "react-router-dom";

function getNodeTextSize(d, quarter) {
  if (quarter) {
    switch (d.data.__typename) {
      case "ROOT":
        return "1em";
      case "Element":
        return ".7em";
      case "Theme":
        return "0.5em";
      case "Principle":
        return "0.4em";
      default:
        throw new Error(`Undefined __typename ${d.data.__typename}`)
    }
  } else {
    switch (d.data.__typename) {
      case "ROOT":
        return "1.35em";
      case "Element":
        return "1.2em";
      case "Theme":
        return "0.9em";
      case "Principle":
        return "0.7em";
      default:
        throw new Error(`Undefined __typename ${d.data.__typename}`)
    }
  }
}

function getNodeRadius(d) {
  let getMultiplier = (d) => {
    switch (d.data.__typename) {
      case "ROOT":
        return 2;
      case "Element":
        return 8;
      case "Theme":
        return 3.4;
      case "Principle":
        return 2.4;
      default:
        throw new Error(`Undefined __typename ${d.data.__typename}`)
    }
  };

  return (parseFloat(d.y) / getMultiplier(d)).toString();
}

function getNodeColour(d) {
  const color1 = "hsl(217,40%,35%)";
  const color2 = "hsl(217,40%,55%)";
  const color3 = "hsl(120,100%,33.3%)";

  switch (d.data.__typename) {
    case "ROOT":
      return "transparent";
    case "Element":
      return color1;
    case "Theme":
      return color2;
    case "Principle":
      return color3;
    default:
      throw new Error(`Undefined __typename ${d.data.__typename}`)
  }
}

function getCircleRadius(d, quarterCircle) {
  if (quarterCircle) {
    switch (d.data.__typename) {
      case "Element":
      case "ROOT":
      case "Theme":
        return 2.5;
      case "Principle":
        return 2;
      default:
        throw new Error(`Undefined __typename ${d.data.__typename}`)

    }
  } else {
    switch (d.data.__typename) {
      case "Element":
      case "ROOT":
      case "Theme":
        return 10;
      case "Principle":
        return 5;
      default:
        throw new Error(`Undefined __typename ${d.data.__typename}`)

    }
  }
}

function makeChart(tree, data, d3, svgElement, width, handleClick, quarterCircle) {
  const root = tree(data);

  const svg = d3.select(svgElement)
    .style("width", "100%")
    .style("padding", "10px")
    .style("box-sizing", "border-box")
    .style("font", quarterCircle ? "normal bold 12px/36px sans-serif" : "normal bold 16px/36px sans-serif");

  const g = svg.append("g");

  g.append("g")
    .attr("fill", "none")
    .attr("stroke", "#adadad")
    .attr("stroke-opacity", 0.4)
    .attr("stroke-width", quarterCircle ? 0.4 : 1.5)
    .selectAll("path")
    .data(root.links())
    .enter().append("path")
    .attr("d", d3.linkRadial()
      .angle(d => quarterCircle ? d.x + 0.25 * Math.PI : d.x)
      .radius(getNodeRadius));

  console.log(handleClick);

  // svg.append('image')
  //   .attr('href', `${grayscaleLogo}`)
  //   .attr('width', 100)
  //   .attr('height', 100)
  //   .attr('opacity', '0.3')
  //   .attr('x', '-41')
  //   .attr('y', '-80');

  const node = g.append("g")
    .attr("stroke-linejoin", "round")
    .attr("stroke-width", 3)
    .selectAll("g")
    .data(root.descendants().reverse())
    .enter().append("g")
    .on("click", handleClick)
    .attr("transform", d => `
        rotate(${d.x * 180 / Math.PI - (quarterCircle ? 45 : 90)})
        translate(${getNodeRadius(d)},0)
      `);

  node.append("circle")
    .attr("fill", getNodeColour)
    .attr("r", d => getCircleRadius(d, quarterCircle))
    .attr("transform", d => quarterCircle ? null : d.data.__typename === "Principle" ? "scale(1.1,1.1)" : null);

  node.append("text")
    .attr("dy", "0.31em")
    .attr("x", d => d.x < Math.PI === true ? 20 : -20)
    .attr("text-anchor", d => d.x < Math.PI === true ? "start" : "end")
    .attr("transform", d => d.x >= Math.PI ? "rotate(180)" : null)
    .attr("font-weight", d => d.data.__typename === "Element" || d.data.__typename === "Root" ? "700" : "500")
    .attr("font-size", d => getNodeTextSize(d, quarterCircle))
    .attr("font-family", d => d.data.__typename === "Element" ? "Helvetica" : "inherit")
    .attr("cursor", "pointer")
    .text(d => d.data.name);

  // document.body.appendChild(svg.node());

  const box = g.node().getBBox();

  svg
    .attr("width", box.width)
    .attr("height", box.height)
    .attr("viewBox", `${box.x} ${box.y} ${box.width} ${box.height}`);

  return svg.node();
}

function makeTree(d3, radius, quarterCircle) {
  return (
    data => d3.tree()
      .size([(quarterCircle ? 0.6 : 2) * Math.PI, quarterCircle ? 750 : radius])
      .separation((a, b) => (a.parent === b.parent ? 1.1 : 1.8) / a.depth)
      (d3.hierarchy(data))
  )
}

let width = 2000;

function getRadius(width) {
  return width / 2;
}

export default class Dendrogram extends Component {
  static propTypes = {
    data: PropTypes.object.isRequired,
    quarterCircle: PropTypes.bool,
  };

  static defaultProps = {
    quarterCircle: false
  };

  constructor(props) {
    super(props);
    this.state = { redirectTo: false };
    this.handleClick = this.handleClick.bind(this);

  }

  handleClick(event) {
    let id = event.data.id;

    if (id) {
      this.setState({ redirectTo: `/elements/${id}` })
    }
  }

  componentDidMount() {
    makeChart(makeTree(d3, getRadius(width), this.props.quarterCircle), {
      name: "",
      children: this.props.data,
      __typename: "ROOT"
    }, d3, document.getElementById("thing"), width, this.handleClick, this.props.quarterCircle)
  }

  render() {
    if (this.state.redirectTo) {
      return <Redirect to={this.state.redirectTo} push />
    }

    return (
      <div className="text-center w-100">
        <div className="dendrogram-wrapper mt-2" style={{ width: '100%', height: 'auto' }}>
          <svg
            id="thing"
            viewBox="-478.082763671875 -557.4598999023438 853.5769653320312 949.1807861328125"
            width={1000}
            height={1000}
            style={{
              width: "100%",
              height: "auto",
              padding: "10px",
              boxSizing: "border-box",
              fontStyle: "normal",
              fontVariantCaps: "normal",
              fontWeight: "normal",
              fontStretch: "normal",
              fontSize: "11px",
              lineHeight: "normal",
              fontFamily: "inherit"
            }}
          />
        </div>

      </div>
    );
  }
}


// WEBPACK FOOTER //
// ./src/Components/DendrogramContainer/Components/Dendrogram.js